import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Target, Calendar, FileText, Plus, 
  CheckCircle, Clock, Zap, Battery, TrendingUp, MessageSquare, Video, Mail,
  Eye, EyeOff, Bell, HelpCircle, Trash2, X, Shield, Sparkles, Loader2, Send,
  Building2, Briefcase, FolderKanban, Star, Globe, ChevronRight, Phone, Edit2, Key, Save, Award,
  Upload, Paperclip, Download
} from "lucide-react";

export const ClientDetailPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [clientFacingNotes, setClientFacingNotes] = useState([]);
  const [caseNotes, setCaseNotes] = useState([]);
  const [replyingNoteId, setReplyingNoteId] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [showClientNoteDialog, setShowClientNoteDialog] = useState(false);
  const [showCaseNoteDialog, setShowCaseNoteDialog] = useState(false);
  
  // Edit client contact state
  const [showEditContactDialog, setShowEditContactDialog] = useState(false);
  const [editContactForm, setEditContactForm] = useState({ name: "", email: "", phone: "", business_name: "" });
  const [savingContact, setSavingContact] = useState(false);
  
  // Edit intake state
  const [showEditIntakeDialog, setShowEditIntakeDialog] = useState(false);
  const [editIntakeForm, setEditIntakeForm] = useState({ purpose: "", motivation: "", primary_business_goal: "", consulting_expectations: "" });
  const [savingIntake, setSavingIntake] = useState(false);
  
  // Password reset state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  
  const [challengeForm, setChallengeForm] = useState({
    title: "",
    description: "",
    due_date: ""
  });
  
  const [clientNoteForm, setClientNoteForm] = useState({
    title: "",
    content: "",
    category: "general",
    attachment: null,
    attachmentName: ""
  });

  const [caseNoteForm, setCaseNoteForm] = useState({
    title: "",
    content: "",
    category: "session",
    follow_up_questions: [],
    reminder_date: "",
    reminder_note: ""
  });

  const [newFollowUp, setNewFollowUp] = useState("");
  const [clientTiers, setClientTiers] = useState({});
  const [rewritingNote, setRewritingNote] = useState(null); // Track which note is being rewritten
  
  // Business & Project state
  const [businesses, setBusinesses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showBusinessDialog, setShowBusinessDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [businessForm, setBusinessForm] = useState({
    name: "",
    industry: "",
    description: "",
    website: "",
    stage: "startup"
  });
  const [projectForm, setProjectForm] = useState({
    title: "",
    project_type: "Other",
    goals: "",
    notes: "",
    business_id: ""
  });
  const [projectTypes, setProjectTypes] = useState([]);

  useEffect(() => {
    fetchClientData();
    fetchClientTiers();
    fetchBusinesses();
    fetchProjects();
    fetchProjectTypes();
  }, [clientId]);

  const fetchClientTiers = async () => {
    try {
      const res = await authFetch(`${API}/client-tiers`, {  });
      if (res.ok) setClientTiers(await res.json());
    } catch (_error) {
      console.error("Error fetching tiers:", _error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const res = await authFetch(`${API}/consultant/client/${clientId}/businesses`, {  });
      if (res.ok) setBusinesses(await res.json());
    } catch (_error) {
      console.error("Error fetching businesses:", _error);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await authFetch(`${API}/consultant/projects`, {  });
      if (res.ok) {
        const allProjects = await res.json();
        // Filter projects for this client
        setProjects(allProjects.filter(p => p.client_id === clientId));
      }
    } catch (_error) {
      console.error("Error fetching projects:", _error);
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const res = await authFetch(`${API}/project-types`, {  });
      if (res.ok) setProjectTypes(await res.json());
    } catch (_error) {
      console.error("Error fetching project types:", _error);
    }
  };

  const handleUpdateClientTier = async (newTier) => {
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/tier`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ tier: newTier })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        // Update local state
        setData({
          ...data,
          client: { ...data.client, tier: newTier }
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update tier");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const getTierBadgeStyle = (tier) => {
    switch(tier) {
      case "basic":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "premium_rapid":
        return "bg-purple-100 text-purple-700 border-purple-200";
      case "growth_strategic":
        return "bg-emerald-100 text-[#39FF14] border-emerald-200";
      case "growth_optimization":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "growth_scale":
        return "bg-rose-100 text-rose-700 border-rose-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const getTierLabel = (tier) => {
    switch(tier) {
      case "basic": return "Basic Consulting";
      case "premium_rapid": return "R.A.P.I.D.™";
      case "growth_strategic": return "Strategic Foundation";
      case "growth_optimization": return "Growth & Optimization";
      case "growth_scale": return "Scale & Leadership";
      default: return "Unassigned";
    }
  };

  const fetchClientData = async () => {
    try {
      const [clientRes, clientNotesRes, caseNotesRes] = await Promise.all([
        authFetch(`${API}/consultant/clients/${clientId}`, {  }),
        authFetch(`${API}/consultant/client-notes/${clientId}`, {  }),
        authFetch(`${API}/consultant/case-notes/${clientId}`, {  })
      ]);
      
      if (clientRes.ok) setData(await clientRes.json());
      if (clientNotesRes.ok) setClientFacingNotes(await clientNotesRes.json());
      if (caseNotesRes.ok) setCaseNotes(await caseNotesRes.json());
      
      if (!clientRes.ok) {
        toast.error("Failed to load client data");
        navigate("/consultant");
      }
    } catch (_error) {
      console.error("Error:", _error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChallenge = async () => {
    try {
      const response = await authFetch(`${API}/consultant/challenges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ ...challengeForm, client_id: clientId })
      });

      if (response.ok) {
        toast.success("Challenge assigned!");
        setShowChallengeDialog(false);
        setChallengeForm({ title: "", description: "", due_date: "" });
        fetchClientData();
      } else {
        toast.error("Failed to assign challenge");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleAddClientNote = async () => {
    try {
      const noteData = {
        client_id: clientId,
        title: clientNoteForm.title,
        content: clientNoteForm.content,
        category: clientNoteForm.category
      };
      
      // Add attachment if present
      if (clientNoteForm.attachment) {
        noteData.attachment_name = clientNoteForm.attachmentName;
        noteData.attachment_data = clientNoteForm.attachment;
        noteData.attachment_type = clientNoteForm.attachmentType;
      }
      
      const response = await authFetch(`${API}/consultant/client-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(noteData)
      });

      if (response.ok) {
        toast.success(clientNoteForm.attachment ? "Note with document shared with client!" : "Note shared with client!");
        setShowClientNoteDialog(false);
        setClientNoteForm({ title: "", content: "", category: "general", attachment: null, attachmentName: "" });
        fetchClientData();
      } else {
        toast.error("Failed to add note");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:xxx;base64, prefix
      setClientNoteForm({
        ...clientNoteForm,
        attachment: base64,
        attachmentName: file.name,
        attachmentType: file.type
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAttachment = () => {
    setClientNoteForm({
      ...clientNoteForm,
      attachment: null,
      attachmentName: "",
      attachmentType: ""
    });
  };

  const handleAddCaseNote = async () => {
    try {
      const response = await authFetch(`${API}/consultant/case-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ ...caseNoteForm, client_id: clientId })
      });

      if (response.ok) {
        toast.success("Case note added!");
        setShowCaseNoteDialog(false);
        setCaseNoteForm({
          title: "",
          content: "",
          category: "session",
          follow_up_questions: [],
          reminder_date: "",
          reminder_note: ""
        });
        fetchClientData();
      } else {
        toast.error("Failed to add case note");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const addFollowUpQuestion = () => {
    if (newFollowUp.trim()) {
      setCaseNoteForm({
        ...caseNoteForm,
        follow_up_questions: [...caseNoteForm.follow_up_questions, { question: newFollowUp.trim(), answered: false }]
      });
      setNewFollowUp("");
    }
  };

  const removeFollowUpQuestion = (index) => {
    setCaseNoteForm({
      ...caseNoteForm,
      follow_up_questions: caseNoteForm.follow_up_questions.filter((_, i) => i !== index)
    });
  };

  const handleAnswerFollowUp = async (caseNoteId, questionIndex, answer) => {
    try {
      const response = await authFetch(`${API}/consultant/case-notes/${caseNoteId}/follow-up/${questionIndex}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ answer })
      });

      if (response.ok) {
        toast.success("Follow-up answered!");
        fetchClientData();
      }
    } catch (_error) {
      toast.error("Failed to save answer");
    }
  };

  const handleMarkReminderComplete = async (caseNoteId) => {
    try {
      const response = await authFetch(`${API}/consultant/case-notes/${caseNoteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ reminder_completed: true })
      });

      if (response.ok) {
        toast.success("Reminder marked complete!");
        fetchClientData();
      }
    } catch (_error) {
      toast.error("Failed to update reminder");
    }
  };

  const handleDeleteCaseNote = async (caseNoteId) => {
    try {
      const response = await authFetch(`${API}/consultant/case-notes/${caseNoteId}`, {
        method: "DELETE",
        
      });

      if (response.ok) {
        toast.success("Case note deleted");
        fetchClientData();
      }
    } catch (_error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteClientNote = async (noteId) => {
    try {
      const response = await authFetch(`${API}/consultant/client-notes/${noteId}`, {
        method: "DELETE",
        
      });

      if (response.ok) {
        toast.success("Note deleted");
        fetchClientData();
      }
    } catch (_error) {
      toast.error("Failed to delete");
    }
  };

  const handleReplyToNote = async (noteId) => {
    if (!replyContent.trim()) return;
    setSendingReply(true);
    try {
      const response = await authFetch(`${API}/consultant/client-notes/${noteId}/respond`, {
        method: "POST",
        body: JSON.stringify({ content: replyContent.trim() }),
      });
      if (response.ok) {
        toast.success("Reply sent to client");
        setReplyContent("");
        setReplyingNoteId(null);
        fetchClientData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to send reply");
      }
    } catch (_error) {
      toast.error("Failed to send reply");
    }
    setSendingReply(false);
  };


  // AI Rewrite function for notes
  const handleAIRewriteNote = async (noteContent, noteType = "general", targetSetter = null) => {
    setRewritingNote(true);
    try {
      const response = await authFetch(`${API}/ai/rewrite-note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          note: noteContent,
          note_type: noteType,
          context: `Client: ${data?.client?.name || 'Unknown'}`
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (targetSetter) {
          targetSetter(result.rewritten);
        }
        toast.success("Note rewritten by AI!");
        return result.rewritten;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to rewrite note");
        return null;
      }
    } catch (_error) {
      toast.error("Failed to rewrite note");
      return null;
    } finally {
      setRewritingNote(false);
    }
  };

  // Rewrite content in case note form
  const handleRewriteCaseNoteForm = async () => {
    if (!caseNoteForm.content.trim()) {
      toast.error("Please enter some content first");
      return;
    }
    const rewritten = await handleAIRewriteNote(caseNoteForm.content, "case_note");
    if (rewritten) {
      setCaseNoteForm({ ...caseNoteForm, content: rewritten });
    }
  };

  // Rewrite content in client note form
  const handleRewriteClientNoteForm = async () => {
    if (!clientNoteForm.content.trim()) {
      toast.error("Please enter some content first");
      return;
    }
    const rewritten = await handleAIRewriteNote(clientNoteForm.content, "general");
    if (rewritten) {
      setClientNoteForm({ ...clientNoteForm, content: rewritten });
    }
  };

  // Business handlers
  const handleCreateBusiness = async () => {
    if (!businessForm.name.trim()) {
      toast.error("Business name is required");
      return;
    }
    try {
      const response = await authFetch(`${API}/consultant/businesses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ ...businessForm, client_id: clientId })
      });

      if (response.ok) {
        toast.success("Business created successfully!");
        setShowBusinessDialog(false);
        setBusinessForm({ name: "", industry: "", description: "", website: "", stage: "startup" });
        fetchBusinesses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create business");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm("Are you sure you want to delete this business?")) return;
    try {
      const response = await authFetch(`${API}/consultant/businesses/${businessId}`, {
        method: "DELETE",
        
      });

      if (response.ok) {
        toast.success("Business deleted");
        fetchBusinesses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete business");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleSetPrimaryBusiness = async (businessId) => {
    try {
      const response = await authFetch(`${API}/consultant/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ is_primary: true })
      });

      if (response.ok) {
        toast.success("Primary business updated");
        fetchBusinesses();
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  // Project handlers
  const handleCreateProject = async () => {
    if (!projectForm.title.trim()) {
      toast.error("Project title is required");
      return;
    }
    try {
      const response = await authFetch(`${API}/consultant/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ 
          ...projectForm, 
          client_id: clientId,
          business_id: projectForm.business_id || null
        })
      });

      if (response.ok) {
        toast.success("Project created successfully!");
        setShowProjectDialog(false);
        setProjectForm({ title: "", project_type: "Other", goals: "", notes: "", business_id: "" });
        fetchProjects();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create project");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleUpdateProjectBusiness = async (projectId, businessId) => {
    try {
      const response = await authFetch(`${API}/consultant/projects/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ business_id: businessId || null })
      });

      if (response.ok) {
        toast.success("Project updated");
        fetchProjects();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update project");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const getBusinessName = (businessId) => {
    if (!businessId) return "Unassigned";
    const business = businesses.find(b => b.business_id === businessId);
    return business?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { intake, weekly_logs, challenges, notes, meetings, ...client } = data || {};
  const pendingChallenges = challenges?.filter(c => !c.completed) || [];
  const completedChallenges = challenges?.filter(c => c.completed) || [];

  const getCategoryColor = (category) => {
    const colors = {
      general: "bg-neutral-100 text-neutral-700",
      action_item: "bg-blue-100 text-blue-700",
      recommendation: "bg-emerald-100 text-[#39FF14]",
      insight: "bg-purple-100 text-purple-700",
      session: "bg-blue-100 text-blue-700",
      observation: "bg-amber-100 text-amber-700",
      strategy: "bg-emerald-100 text-[#39FF14]",
      concern: "bg-red-100 text-red-700"
    };
    return colors[category] || colors.general;
  };

  // Handle edit contact info
  const handleEditContact = () => {
    setEditContactForm({
      name: client?.name || "",
      email: client?.email || "",
      phone: client?.phone || "",
      business_name: client?.business_name || ""
    });
    setShowEditContactDialog(true);
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/contact`, {
        method: "PUT",
        body: JSON.stringify(editContactForm)
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Contact information updated");
        setShowEditContactDialog(false);
        // Refresh client data to show updated info
        await fetchClientData();
      } else {
        console.error("Update failed:", result);
        toast.error(result.detail || "Failed to update contact");
      }
    } catch (_error) {
      console.error("Save contact _error:", _error);
      toast.error("Failed to update contact");
    } finally {
      setSavingContact(false);
    }
  };

  // Handle edit intake
  const handleEditIntake = () => {
    setEditIntakeForm({
      purpose: intake?.purpose || "",
      motivation: intake?.motivation || "",
      primary_business_goal: intake?.primary_business_goal || "",
      consulting_expectations: intake?.consulting_expectations || ""
    });
    setShowEditIntakeDialog(true);
  };

  const handleSaveIntake = async () => {
    setSavingIntake(true);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/intake`, {
        method: "PUT",
        body: JSON.stringify(editIntakeForm)
      });
      if (response.ok) {
        toast.success("Intake information updated");
        setShowEditIntakeDialog(false);
        fetchClientData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update intake");
      }
    } catch (_error) {
      toast.error("Failed to update intake");
    } finally {
      setSavingIntake(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setResettingPassword(true);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ new_password: newPassword })
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Password reset successfully");
        setShowPasswordDialog(false);
        setNewPassword("");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to reset password");
      }
    } catch (_error) {
      toast.error("Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="client-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/consultant")}
                className="text-neutral-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  {client?.picture ? (
                    <img src={client.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <span className="text-[#39FF14] font-semibold">{client?.name?.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-neutral-900">{client?.name}</h1>
                  <div className="flex items-center space-x-4 text-sm text-neutral-500">
                    <span className="flex items-center">
                      <Mail className="w-3 h-3 mr-1" />
                      {client?.email}
                    </span>
                    {client?.phone && (
                      <span className="flex items-center">
                        <Phone className="w-3 h-3 mr-1" />
                        {client?.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Badge 
                variant={client?.onboarded ? "default" : "secondary"}
                className={client?.onboarded ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}
              >
                {client?.onboarded ? "Active" : "Pending Onboard"}
              </Badge>
              
              {/* R.A.P.I.D. Results & Certificate for R.A.P.I.D. clients */}
              {(client?.tier === "premium_rapid" || client?.rapid_access) && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                    onClick={() => navigate(`/consultant/rapid/${clientId}`)}
                    data-testid="view-rapid-results-btn"
                  >
                    <Target className="w-4 h-4 mr-1" />
                    R.A.P.I.D. Results
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                    onClick={async () => {
                      try {
                        const response = await authFetch(`${API}/certificates/rapid/${clientId}`);
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `RAPID_Certificate_${client?.name?.replace(/\s+/g, '_') || 'Client'}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success("Certificate downloaded!");
                        } else {
                          const err = await response.json();
                          toast.error(err.detail || "Certificate not available yet");
                        }
                      } catch (_error) {
                        toast.error("Failed to download certificate");
                      }
                    }}
                    data-testid="download-certificate-btn"
                  >
                    <Award className="w-4 h-4 mr-1" />
                    Certificate
                  </Button>
                </>
              )}
              
              {/* Tier Management Dropdown */}
              <Select
                value={client?.tier || "unassigned"}
                onValueChange={(value) => handleUpdateClientTier(value === "unassigned" ? null : value)}
              >
                <SelectTrigger 
                  className={`w-48 h-9 text-sm font-medium border ${getTierBadgeStyle(client?.tier)}`}
                  data-testid="client-tier-select"
                >
                  <Shield className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="Set Tier">{getTierLabel(client?.tier)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="basic">Basic Consulting</SelectItem>
                  <SelectItem value="premium_rapid">R.A.P.I.D.™ Program</SelectItem>
                  <SelectItem value="growth_strategic">Strategic Foundation ($15k)</SelectItem>
                  <SelectItem value="growth_optimization">Growth & Optimization ($25k)</SelectItem>
                  <SelectItem value="growth_scale">Scale & Leadership ($40k)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-neutral-200 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]">
              Overview
            </TabsTrigger>
            <TabsTrigger value="businesses" className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]">
              <Building2 className="w-4 h-4 mr-1" />
              Businesses ({businesses.length})
            </TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]">
              <FolderKanban className="w-4 h-4 mr-1" />
              Projects ({projects.length})
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]">
              Logs ({weekly_logs?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="client-notes" className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]">
              <Eye className="w-4 h-4 mr-1" />
              Client Notes ({clientFacingNotes.length})
            </TabsTrigger>
            <TabsTrigger value="case-notes" className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]">
              <EyeOff className="w-4 h-4 mr-1" />
              Case Notes ({caseNotes.length})
            </TabsTrigger>
            <TabsTrigger value="challenges" className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]">
              Challenges ({challenges?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Client Contact Card */}
            <Card className="border-[#39FF14]/20 bg-gradient-to-r from-green-50 to-emerald-50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Mail className="w-5 h-5 mr-2 text-[#39FF14]" />
                    Client Contact Information
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleEditContact} data-testid="edit-contact-btn">
                      <Edit2 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowPasswordDialog(true)} className="text-amber-600 border-amber-300 hover:bg-amber-50" data-testid="reset-password-btn">
                      <Key className="w-4 h-4 mr-1" /> Reset Password
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-green-100">
                    <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#39FF14]" />
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Email</p>
                      <a href={`mailto:${client?.email}`} className="text-sm font-medium text-[#333333] hover:text-[#39FF14]">
                        {client?.email || "Not provided"}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-green-100">
                    <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#39FF14]" />
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Phone</p>
                      <a href={`tel:${client?.phone}`} className="text-sm font-medium text-[#333333] hover:text-[#39FF14]">
                        {client?.phone || "Not provided"}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-green-100">
                    <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#39FF14]" />
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Business</p>
                      <p className="text-sm font-medium text-[#333333]">
                        {client?.business_name || "Not provided"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Intake Info */}
              <div className="lg:col-span-2">
                <Card className="border-neutral-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-[#39FF14]" />
                        Intake Information
                      </CardTitle>
                      <Button variant="outline" size="sm" onClick={handleEditIntake} data-testid="edit-intake-btn">
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {intake ? (
                      <div className="space-y-6">
                        {/* Business Information Section */}
                        {(intake.business_name || intake.business_type || intake.industry) && (
                          <div>
                            <h4 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center">
                              <Building2 className="w-4 h-4 mr-2 text-emerald-600" />
                              Business Information
                            </h4>
                            <div className="grid grid-cols-2 gap-4 pl-6">
                              {intake.business_name && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Business Name</p>
                                  <p className="text-neutral-900 text-sm">{intake.business_name}</p>
                                </div>
                              )}
                              {intake.business_type && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Business Type</p>
                                  <p className="text-neutral-900 text-sm">{intake.business_type}</p>
                                </div>
                              )}
                              {intake.industry && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Industry</p>
                                  <p className="text-neutral-900 text-sm">{intake.industry}</p>
                                </div>
                              )}
                              {intake.years_in_business && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Years in Business</p>
                                  <p className="text-neutral-900 text-sm">{intake.years_in_business} years</p>
                                </div>
                              )}
                              {intake.revenue_range && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Revenue Range</p>
                                  <p className="text-neutral-900 text-sm">{intake.revenue_range}</p>
                                </div>
                              )}
                              {intake.employee_count && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Employee Count</p>
                                  <p className="text-neutral-900 text-sm">{intake.employee_count}</p>
                                </div>
                              )}
                              {intake.website && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Website</p>
                                  <a href={intake.website} target="_blank" rel="noopener noreferrer" className="text-emerald-600 text-sm hover:underline">{intake.website}</a>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Contact Information Section */}
                        {(intake.full_name || intake.phone || intake.preferred_contact) && (
                          <div>
                            <h4 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center">
                              <Phone className="w-4 h-4 mr-2 text-blue-600" />
                              Contact Preferences
                            </h4>
                            <div className="grid grid-cols-2 gap-4 pl-6">
                              {intake.phone && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Phone</p>
                                  <p className="text-neutral-900 text-sm">{intake.phone}</p>
                                </div>
                              )}
                              {intake.preferred_contact && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Preferred Contact Method</p>
                                  <p className="text-neutral-900 text-sm capitalize">{intake.preferred_contact}</p>
                                </div>
                              )}
                              {intake.timezone && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Timezone</p>
                                  <p className="text-neutral-900 text-sm">{intake.timezone}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Business Goals Section */}
                        <div>
                          <h4 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center">
                            <Target className="w-4 h-4 mr-2 text-amber-600" />
                            Business Goals & Challenges
                          </h4>
                          <div className="space-y-3 pl-6">
                            <div>
                              <p className="text-xs text-neutral-500 mb-1">Purpose</p>
                              <p className="text-neutral-900 text-sm">{intake.purpose || "Not provided"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-neutral-500 mb-1">Motivation</p>
                              <p className="text-neutral-900 text-sm">{intake.motivation || "Not provided"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-neutral-500 mb-1">Primary Business Goal</p>
                              <p className="text-neutral-900 text-sm">{intake.primary_business_goal || "Not provided"}</p>
                            </div>
                            {intake.specific_challenges && intake.specific_challenges.length > 0 && (
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Specific Challenges</p>
                                <ul className="list-disc list-inside text-sm text-neutral-900">
                                  {intake.specific_challenges.map((challenge, idx) => (
                                    <li key={idx}>{challenge}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {intake.desired_outcomes && intake.desired_outcomes.length > 0 && (
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Desired Outcomes</p>
                                <ul className="list-disc list-inside text-sm text-neutral-900">
                                  {intake.desired_outcomes.map((outcome, idx) => (
                                    <li key={idx}>{outcome}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {intake.biggest_bottleneck && (
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Biggest Bottleneck</p>
                                <p className="text-neutral-900 text-sm">{intake.biggest_bottleneck}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Current State Section */}
                        {(intake.current_revenue || intake.target_revenue || intake.hours_working_per_week) && (
                          <div>
                            <h4 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center">
                              <TrendingUp className="w-4 h-4 mr-2 text-purple-600" />
                              Current State & Goals
                            </h4>
                            <div className="grid grid-cols-2 gap-4 pl-6">
                              {intake.current_revenue && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Current Revenue</p>
                                  <p className="text-neutral-900 text-sm">{intake.current_revenue}</p>
                                </div>
                              )}
                              {intake.target_revenue && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Target Revenue</p>
                                  <p className="text-neutral-900 text-sm">{intake.target_revenue}</p>
                                </div>
                              )}
                              {intake.hours_working_per_week && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Hours Working/Week</p>
                                  <p className="text-neutral-900 text-sm">{intake.hours_working_per_week} hrs</p>
                                </div>
                              )}
                              {intake.ideal_hours_per_week && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Ideal Hours/Week</p>
                                  <p className="text-neutral-900 text-sm">{intake.ideal_hours_per_week} hrs</p>
                                </div>
                              )}
                              {intake.timeline_urgency && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Timeline/Urgency</p>
                                  <p className="text-neutral-900 text-sm">{intake.timeline_urgency.replace(/_/g, ' ')}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Consulting Expectations Section */}
                        <div>
                          <h4 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center">
                            <Briefcase className="w-4 h-4 mr-2 text-indigo-600" />
                            Consulting Expectations
                          </h4>
                          <div className="space-y-3 pl-6">
                            <div>
                              <p className="text-xs text-neutral-500 mb-1">Expectations</p>
                              <p className="text-neutral-900 text-sm">{intake.consulting_expectations || "Not provided"}</p>
                            </div>
                            {intake.previous_consulting_experience && (
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Previous Consulting Experience</p>
                                <p className="text-neutral-900 text-sm">{intake.previous_consulting_experience}</p>
                              </div>
                            )}
                            {intake.budget_range && (
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Budget Range</p>
                                <p className="text-neutral-900 text-sm">{intake.budget_range}</p>
                              </div>
                            )}
                            {intake.commitment_level && (
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Commitment Level</p>
                                <p className="text-neutral-900 text-sm">{intake.commitment_level}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Additional Info */}
                        {(intake.how_did_you_hear || intake.additional_notes) && (
                          <div>
                            <h4 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center">
                              <MessageSquare className="w-4 h-4 mr-2 text-neutral-600" />
                              Additional Information
                            </h4>
                            <div className="space-y-3 pl-6">
                              {intake.how_did_you_hear && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">How Did You Hear About Us?</p>
                                  <p className="text-neutral-900 text-sm">{intake.how_did_you_hear}</p>
                                </div>
                              )}
                              {intake.additional_notes && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Additional Notes</p>
                                  <p className="text-neutral-900 text-sm">{intake.additional_notes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Commitment Status */}
                        {intake.commitment_agreed && (
                          <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                              <span className="text-sm text-emerald-700 font-medium">Commitment Agreement Signed</span>
                              {intake.commitment_timestamp && (
                                <span className="text-xs text-emerald-600">
                                  on {new Date(intake.commitment_timestamp).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-neutral-500 mb-3">No intake form submitted yet</p>
                        <Button variant="outline" size="sm" onClick={handleEditIntake}>
                          <Plus className="w-4 h-4 mr-1" /> Add Intake Info
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="space-y-4">
                <Dialog open={showChallengeDialog} onOpenChange={setShowChallengeDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-amber-500 hover:bg-amber-600" data-testid="assign-challenge-btn">
                      <Target className="w-4 h-4 mr-2" />
                      Assign Challenge
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Assign Challenge</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={challengeForm.title}
                          onChange={(e) => setChallengeForm({ ...challengeForm, title: e.target.value })}
                          placeholder="Challenge title"
                          data-testid="challenge-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={challengeForm.description}
                          onChange={(e) => setChallengeForm({ ...challengeForm, description: e.target.value })}
                          placeholder="Challenge description"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Due Date</Label>
                        <Input
                          type="date"
                          value={challengeForm.due_date}
                          onChange={(e) => setChallengeForm({ ...challengeForm, due_date: e.target.value })}
                        />
                      </div>
                      <Button onClick={handleCreateChallenge} className="w-full bg-amber-500 hover:bg-amber-600">
                        Assign Challenge
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={showClientNoteDialog} onOpenChange={setShowClientNoteDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full border-[#39FF14]/50 text-[#39FF14]" data-testid="add-client-note-btn">
                      <Eye className="w-4 h-4 mr-2" />
                      Share Note with Client
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Share Note with Client</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="bg-[#39FF14]/10 p-3 rounded-lg text-sm text-[#39FF14]">
                        <Eye className="w-4 h-4 inline mr-2" />
                        This note will be visible to the client and they'll be notified via email.
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select 
                          value={clientNoteForm.category} 
                          onValueChange={(v) => setClientNoteForm({ ...clientNoteForm, category: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="action_item">Action Item</SelectItem>
                            <SelectItem value="recommendation">Recommendation</SelectItem>
                            <SelectItem value="insight">Insight</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={clientNoteForm.title}
                          onChange={(e) => setClientNoteForm({ ...clientNoteForm, title: e.target.value })}
                          placeholder="Note title"
                          data-testid="client-note-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Content</Label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handleRewriteClientNoteForm}
                            disabled={rewritingNote || !clientNoteForm.content.trim()}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                            data-testid="ai-rewrite-client-note-btn"
                          >
                            {rewritingNote ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3 mr-1" />
                            )}
                            AI Rewrite
                          </Button>
                        </div>
                        <Textarea
                          value={clientNoteForm.content}
                          onChange={(e) => setClientNoteForm({ ...clientNoteForm, content: e.target.value })}
                          placeholder="Write your note..."
                          className="min-h-32"
                          data-testid="client-note-content-input"
                        />
                      </div>
                      
                      {/* File Attachment Section */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4" />
                          Attach Document (optional)
                        </Label>
                        {clientNoteForm.attachmentName ? (
                          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <span className="text-sm font-medium text-blue-800 truncate max-w-[200px]">
                                {clientNoteForm.attachmentName}
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleRemoveAttachment}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-neutral-300 rounded-lg p-4 text-center hover:border-[#39FF14] transition-colors">
                            <input
                              type="file"
                              id="note-file-upload"
                              className="hidden"
                              onChange={handleFileSelect}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg"
                            />
                            <label htmlFor="note-file-upload" className="cursor-pointer">
                              <Upload className="w-8 h-8 mx-auto text-neutral-400 mb-2" />
                              <p className="text-sm text-neutral-600">Click to upload a document</p>
                              <p className="text-xs text-neutral-400 mt-1">PDF, Word, Excel, PowerPoint, Images (max 5MB)</p>
                            </label>
                          </div>
                        )}
                      </div>
                      
                      <Button onClick={handleAddClientNote} className="w-full bg-[#39FF14] hover:bg-[#32E010] text-black font-medium">
                        <Mail className="w-4 h-4 mr-2" />
                        {clientNoteForm.attachmentName ? "Share Note & Document" : "Share & Notify Client"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={showCaseNoteDialog} onOpenChange={setShowCaseNoteDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full border-neutral-300" data-testid="add-case-note-btn">
                      <EyeOff className="w-4 h-4 mr-2" />
                      Add Private Case Note
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add Private Case Note</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                      <div className="bg-neutral-100 p-3 rounded-lg text-sm text-neutral-600">
                        <EyeOff className="w-4 h-4 inline mr-2" />
                        This note is private and only visible to consultants.
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select 
                          value={caseNoteForm.category} 
                          onValueChange={(v) => setCaseNoteForm({ ...caseNoteForm, category: v })}
                        >
                          <SelectTrigger data-testid="case-note-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="session">Session Notes</SelectItem>
                            <SelectItem value="observation">Observation</SelectItem>
                            <SelectItem value="strategy">Strategy</SelectItem>
                            <SelectItem value="concern">Concern</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={caseNoteForm.title}
                          onChange={(e) => setCaseNoteForm({ ...caseNoteForm, title: e.target.value })}
                          placeholder="Note title"
                          data-testid="case-note-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Content</Label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handleRewriteCaseNoteForm}
                            disabled={rewritingNote || !caseNoteForm.content.trim()}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                            data-testid="ai-rewrite-case-note-btn"
                          >
                            {rewritingNote ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3 mr-1" />
                            )}
                            AI Rewrite
                          </Button>
                        </div>
                        <Textarea
                          value={caseNoteForm.content}
                          onChange={(e) => setCaseNoteForm({ ...caseNoteForm, content: e.target.value })}
                          placeholder="Write your private notes..."
                          className="min-h-32"
                          data-testid="case-note-content-input"
                        />
                      </div>

                      {/* Follow-up Questions */}
                      <div className="space-y-2">
                        <Label className="flex items-center">
                          <HelpCircle className="w-4 h-4 mr-1" />
                          Follow-up Questions
                        </Label>
                        <div className="space-y-2">
                          {caseNoteForm.follow_up_questions.map((q, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded text-sm">
                              <span>{q.question}</span>
                              <button onClick={() => removeFollowUpQuestion(idx)} className="text-neutral-400 hover:text-red-500">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex space-x-2">
                          <Input
                            value={newFollowUp}
                            onChange={(e) => setNewFollowUp(e.target.value)}
                            onKeyPress={(e) => e.key === "Enter" && addFollowUpQuestion()}
                            placeholder="Add a follow-up question..."
                            className="text-sm"
                            data-testid="follow-up-question-input"
                          />
                          <Button size="sm" onClick={addFollowUpQuestion} variant="outline">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Reminder */}
                      <div className="space-y-2 pt-2 border-t">
                        <Label className="flex items-center">
                          <Bell className="w-4 h-4 mr-1" />
                          Set Reminder (Optional)
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={caseNoteForm.reminder_date}
                            onChange={(e) => setCaseNoteForm({ ...caseNoteForm, reminder_date: e.target.value })}
                            data-testid="reminder-date-input"
                          />
                          <Input
                            value={caseNoteForm.reminder_note}
                            onChange={(e) => setCaseNoteForm({ ...caseNoteForm, reminder_note: e.target.value })}
                            placeholder="Reminder note"
                          />
                        </div>
                      </div>

                      <Button onClick={handleAddCaseNote} className="w-full bg-neutral-900 hover:bg-neutral-800" data-testid="save-case-note-btn">
                        Save Case Note
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Activity Summary */}
                <Card className="border-neutral-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Activity Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Weekly Logs</span>
                      <span className="font-mono text-neutral-900">{weekly_logs?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Active Challenges</span>
                      <span className="font-mono text-neutral-900">{pendingChallenges.length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Client Notes</span>
                      <span className="font-mono text-neutral-900">{clientFacingNotes.length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Case Notes</span>
                      <span className="font-mono text-neutral-900">{caseNotes.length}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Businesses Tab */}
          <TabsContent value="businesses" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-neutral-900">Client Businesses</h3>
              <Dialog open={showBusinessDialog} onOpenChange={setShowBusinessDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#39FF14] hover:bg-[#32E010]" data-testid="add-business-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Business
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Business</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Business Name *</Label>
                      <Input
                        value={businessForm.name}
                        onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                        placeholder="e.g., Acme Corp"
                        data-testid="business-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Industry</Label>
                      <Input
                        value={businessForm.industry}
                        onChange={(e) => setBusinessForm({ ...businessForm, industry: e.target.value })}
                        placeholder="e.g., Technology, Healthcare, Retail"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={businessForm.description}
                        onChange={(e) => setBusinessForm({ ...businessForm, description: e.target.value })}
                        placeholder="Brief description of the business..."
                        className="min-h-20"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          value={businessForm.website}
                          onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stage</Label>
                        <Select 
                          value={businessForm.stage} 
                          onValueChange={(v) => setBusinessForm({ ...businessForm, stage: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="startup">Startup</SelectItem>
                            <SelectItem value="growth">Growth</SelectItem>
                            <SelectItem value="established">Established</SelectItem>
                            <SelectItem value="scaling">Scaling</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={handleCreateBusiness} className="w-full bg-[#39FF14] hover:bg-[#32E010]">
                      <Building2 className="w-4 h-4 mr-2" />
                      Create Business
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {businesses.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No businesses added yet</p>
                  <p className="text-sm text-neutral-400 mt-1">Add a business to organize projects and track progress</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {businesses.map((business) => (
                  <Card key={business.business_id} className={`border-neutral-200 ${business.is_primary ? 'ring-2 ring-[#39FF14]' : ''}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-[#39FF14]" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-neutral-900">{business.name}</h4>
                            <p className="text-sm text-neutral-500">{business.industry || "No industry set"}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {business.is_primary && (
                            <Badge className="bg-emerald-100 text-[#39FF14]">
                              <Star className="w-3 h-3 mr-1" />
                              Primary
                            </Badge>
                          )}
                          <Badge variant="outline">{business.stage}</Badge>
                        </div>
                      </div>
                      
                      {business.description && (
                        <p className="text-sm text-neutral-600 mb-3">{business.description}</p>
                      )}
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-4">
                          {business.website && (
                            <a href={business.website} target="_blank" rel="noopener noreferrer" 
                               className="flex items-center text-blue-600 hover:text-blue-700">
                              <Globe className="w-4 h-4 mr-1" />
                              Website
                            </a>
                          )}
                          <span className="text-neutral-500">
                            <FolderKanban className="w-4 h-4 inline mr-1" />
                            {business.project_count || 0} projects
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {!business.is_primary && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleSetPrimaryBusiness(business.business_id)}
                              className="text-amber-600 hover:text-amber-700"
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteBusiness(business.business_id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-neutral-900">Client Projects</h3>
              <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-project-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Project</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Project Title *</Label>
                      <Input
                        value={projectForm.title}
                        onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                        placeholder="e.g., Website Redesign"
                        data-testid="project-title-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Project Type</Label>
                        <Select 
                          value={projectForm.project_type} 
                          onValueChange={(v) => setProjectForm({ ...projectForm, project_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {projectTypes.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Assign to Business</Label>
                        <Select 
                          value={projectForm.business_id || "unassigned"} 
                          onValueChange={(v) => setProjectForm({ ...projectForm, business_id: v === "unassigned" ? "" : v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select business..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {businesses.map((biz) => (
                              <SelectItem key={biz.business_id} value={biz.business_id}>
                                {biz.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Goals</Label>
                      <Textarea
                        value={projectForm.goals}
                        onChange={(e) => setProjectForm({ ...projectForm, goals: e.target.value })}
                        placeholder="What are the main objectives?"
                        className="min-h-20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={projectForm.notes}
                        onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })}
                        placeholder="Any additional notes..."
                        className="min-h-16"
                      />
                    </div>
                    <Button onClick={handleCreateProject} className="w-full bg-blue-600 hover:bg-blue-700">
                      <FolderKanban className="w-4 h-4 mr-2" />
                      Create Project
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {projects.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <FolderKanban className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No projects yet</p>
                  <p className="text-sm text-neutral-400 mt-1">Create a project to track work for this client</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {projects.map((project) => (
                  <Card key={project.project_id} className="border-neutral-200 hover:border-neutral-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            project.status === 'completed' ? 'bg-green-100' : 
                            project.status === 'on_hold' ? 'bg-amber-100' : 'bg-blue-100'
                          }`}>
                            <Briefcase className={`w-5 h-5 ${
                              project.status === 'completed' ? 'text-green-700' : 
                              project.status === 'on_hold' ? 'text-amber-700' : 'text-blue-700'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold text-neutral-900">{project.title}</h4>
                              <Badge variant="outline" className="text-xs">{project.project_type}</Badge>
                              <Badge className={`text-xs ${
                                project.status === 'completed' ? 'bg-green-100 text-green-700' : 
                                project.status === 'on_hold' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {project.status}
                              </Badge>
                            </div>
                            <div className="flex items-center space-x-3 mt-1 text-sm text-neutral-500">
                              <span className="flex items-center">
                                <Building2 className="w-3 h-3 mr-1" />
                                {getBusinessName(project.business_id)}
                              </span>
                              <span>{project.percentage_complete}% complete</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Select 
                            value={project.business_id || "unassigned"} 
                            onValueChange={(v) => handleUpdateProjectBusiness(project.project_id, v === "unassigned" ? null : v)}
                          >
                            <SelectTrigger className="w-40 h-8 text-xs">
                              <SelectValue placeholder="Assign business" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {businesses.map((biz) => (
                                <SelectItem key={biz.business_id} value={biz.business_id}>
                                  {biz.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/consultant/projects/${project.project_id}`)}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {project.goals && (
                        <p className="text-sm text-neutral-600 mt-3 pl-14 line-clamp-2">{project.goals}</p>
                      )}
                      
                      {/* Progress Updates Section */}
                      {project.progress_updates && project.progress_updates.length > 0 && (
                        <div className="mt-4 pl-14 border-t pt-4">
                          <h5 className="text-sm font-medium text-neutral-700 mb-2 flex items-center">
                            <TrendingUp className="w-4 h-4 mr-1 text-blue-500" />
                            Recent Updates ({project.progress_updates.length})
                          </h5>
                          <div className="space-y-2">
                            {project.progress_updates.slice(-3).reverse().map((update, idx) => (
                              <div key={update.update_id || idx} className="p-3 bg-neutral-50 rounded-lg text-sm">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-neutral-500">
                                    {new Date(update.created_at).toLocaleDateString()}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {update.percentage_complete}% complete
                                  </Badge>
                                </div>
                                {update.update_text && (
                                  <p className="text-neutral-700">{update.update_text}</p>
                                )}
                                {update.tasks_completed && update.tasks_completed.length > 0 && (
                                  <div className="mt-2">
                                    <span className="text-xs text-green-600 font-medium">Completed:</span>
                                    <ul className="text-xs text-neutral-600 ml-2">
                                      {update.tasks_completed.map((task, i) => (
                                        <li key={i} className="flex items-center">
                                          <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                                          {task}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {update.tasks_upcoming && update.tasks_upcoming.length > 0 && (
                                  <div className="mt-1">
                                    <span className="text-xs text-blue-600 font-medium">Upcoming:</span>
                                    <ul className="text-xs text-neutral-600 ml-2">
                                      {update.tasks_upcoming.map((task, i) => (
                                        <li key={i} className="flex items-center">
                                          <Clock className="w-3 h-3 mr-1 text-blue-500" />
                                          {task}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {update.blockers && (
                                  <div className="mt-1 text-xs text-red-600">
                                    <span className="font-medium">Blockers:</span> {update.blockers}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Weekly Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            {weekly_logs?.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No weekly logs yet</p>
                </CardContent>
              </Card>
            ) : (
              weekly_logs?.map((log) => (
                <Card key={log.log_id} className="border-neutral-200">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-neutral-900">Week of {log.week_start}</h3>
                        <p className="text-sm text-neutral-500">
                          Logged on {new Date(log.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center text-amber-600">
                          <Zap className="w-4 h-4 mr-1" />
                          {log.focus_rating}/10
                        </div>
                        <div className="flex items-center text-green-600">
                          <Battery className="w-4 h-4 mr-1" />
                          {log.energy_rating}/10
                        </div>
                        <div className="flex items-center text-blue-600">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          {log.momentum_rating}/10
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-amber-50 rounded-lg p-3">
                        <p className="text-xs text-amber-700 mb-1">Accomplishments</p>
                        <p className="text-xl font-semibold text-amber-900">{log.accomplishments?.length || 0}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-xs text-green-700 mb-1">Tasks Done</p>
                        <p className="text-xl font-semibold text-green-900">{log.completed_tasks?.length || 0}</p>
                      </div>
                      <div className="bg-pink-50 rounded-lg p-3">
                        <p className="text-xs text-pink-700 mb-1">Self-Care</p>
                        <p className="text-xl font-semibold text-pink-900">{log.self_care_activities?.length || 0}</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-3">
                        <p className="text-xs text-yellow-700 mb-1">Clarity</p>
                        <p className="text-xl font-semibold text-yellow-900">{log.moments_of_clarity?.length || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Client-Facing Notes Tab */}
          <TabsContent value="client-notes" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowClientNoteDialog(true)} className="bg-[#39FF14] hover:bg-[#32E010]">
                <Plus className="w-4 h-4 mr-2" />
                Share New Note
              </Button>
            </div>

            {clientFacingNotes.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <Eye className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No notes shared with client yet</p>
                </CardContent>
              </Card>
            ) : (
              clientFacingNotes.map((note) => (
                <Card key={note.note_id} className={`border-emerald-200 ${note.responses?.length > 0 ? 'ring-2 ring-blue-300' : 'bg-[#39FF14]/10/30'}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-neutral-900">{note.title}</h4>
                          <Badge className={getCategoryColor(note.category)}>{note.category}</Badge>
                          {note.responses?.length > 0 && (
                            <Badge className="bg-blue-100 text-blue-700 animate-pulse">
                              <MessageSquare className="w-3 h-3 mr-1" />
                              {note.responses.length} Response{note.responses.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-neutral-500">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-emerald-100 text-[#39FF14]">
                          <Eye className="w-3 h-3 mr-1" />
                          Visible to Client
                        </Badge>
                        <button onClick={() => handleDeleteClientNote(note.note_id)} className="text-neutral-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-neutral-700 whitespace-pre-wrap">{note.content}</p>

                    {/* Client Responses */}
                    {note.responses?.length > 0 && (
                      <div className="mt-4 space-y-2 border-t border-blue-200 pt-3">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Conversation</p>
                        {note.responses.map((resp, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border ${resp.author_role === 'consultant' ? 'bg-emerald-50 border-emerald-100' : 'bg-blue-50 border-blue-100'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-semibold ${resp.author_role === 'consultant' ? 'text-emerald-700' : 'text-blue-700'}`}>
                                {resp.author_role === 'consultant' ? 'You' : resp.author_name || 'Client'}
                              </span>
                              <span className="text-[10px] text-neutral-400">{resp.created_at ? new Date(resp.created_at).toLocaleString() : ''}</span>
                            </div>
                            <p className="text-sm text-neutral-800 whitespace-pre-wrap">{resp.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply Form */}
                    {replyingNoteId === note.note_id ? (
                      <div className="mt-3 pt-3 border-t border-neutral-200 space-y-2">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Type your reply to the client..."
                          className="w-full p-3 border border-neutral-300 rounded-lg text-sm min-h-[80px] focus:ring-2 focus:ring-[#39FF14]/40 focus:border-[#39FF14] outline-none"
                          data-testid={`reply-input-${note.note_id}`}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => { setReplyingNoteId(null); setReplyContent(""); }}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleReplyToNote(note.note_id)} disabled={sendingReply || !replyContent.trim()}
                            className="bg-[#333333] text-white" data-testid={`send-reply-${note.note_id}`}>
                            {sendingReply ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                            Send Reply
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 pt-2 border-t border-neutral-100">
                        <Button size="sm" variant="ghost" className="text-blue-600 hover:bg-blue-50"
                          onClick={() => { setReplyingNoteId(note.note_id); setReplyContent(""); }}
                          data-testid={`reply-btn-${note.note_id}`}>
                          <MessageSquare className="w-3.5 h-3.5 mr-1" /> Reply to Client
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Private Case Notes Tab */}
          <TabsContent value="case-notes" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowCaseNoteDialog(true)} className="bg-neutral-900 hover:bg-neutral-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Case Note
              </Button>
            </div>

            {caseNotes.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <EyeOff className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No private case notes yet</p>
                </CardContent>
              </Card>
            ) : (
              caseNotes.map((note) => (
                <Card key={note.case_note_id} className="border-neutral-200">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-neutral-900">{note.title}</h4>
                          <Badge className={getCategoryColor(note.category)}>{note.category}</Badge>
                        </div>
                        <p className="text-xs text-neutral-500">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-neutral-500">
                          <EyeOff className="w-3 h-3 mr-1" />
                          Private
                        </Badge>
                        <button onClick={() => handleDeleteCaseNote(note.case_note_id)} className="text-neutral-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-neutral-700 whitespace-pre-wrap mb-4">{note.content}</p>

                    {/* Reminder */}
                    {note.reminder_date && (
                      <div className={`p-3 rounded-lg mb-4 ${note.reminder_completed ? 'bg-green-50' : 'bg-amber-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Bell className={`w-4 h-4 mr-2 ${note.reminder_completed ? 'text-green-600' : 'text-amber-600'}`} />
                            <span className="text-sm font-medium">
                              Reminder: {note.reminder_date}
                            </span>
                            {note.reminder_note && (
                              <span className="text-sm text-neutral-600 ml-2">- {note.reminder_note}</span>
                            )}
                          </div>
                          {!note.reminder_completed && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleMarkReminderComplete(note.case_note_id)}
                              className="text-amber-700 hover:bg-amber-100"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Complete
                            </Button>
                          )}
                          {note.reminder_completed && (
                            <Badge className="bg-green-100 text-green-700">Completed</Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Follow-up Questions */}
                    {note.follow_up_questions?.length > 0 && (
                      <div className="border-t pt-4 mt-4">
                        <h5 className="text-sm font-medium text-neutral-700 mb-3 flex items-center">
                          <HelpCircle className="w-4 h-4 mr-1" />
                          Follow-up Questions
                        </h5>
                        <div className="space-y-3">
                          {note.follow_up_questions.map((q, idx) => (
                            <div key={idx} className={`p-3 rounded-lg ${q.answered ? 'bg-green-50' : 'bg-blue-50'}`}>
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-neutral-800">{q.question}</p>
                                {q.answered ? (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Answered</Badge>
                                ) : (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">Pending</Badge>
                                )}
                              </div>
                              {q.answered && q.answer && (
                                <p className="text-sm text-neutral-600 mt-2 pl-4 border-l-2 border-green-300">{q.answer}</p>
                              )}
                              {!q.answered && (
                                <div className="mt-2 flex space-x-2">
                                  <Input 
                                    placeholder="Enter answer..." 
                                    className="text-sm"
                                    id={`answer-${note.case_note_id}-${idx}`}
                                  />
                                  <Button 
                                    size="sm"
                                    onClick={() => {
                                      const input = document.getElementById(`answer-${note.case_note_id}-${idx}`);
                                      handleAnswerFollowUp(note.case_note_id, idx, input.value);
                                    }}
                                    className="bg-blue-500 hover:bg-blue-600"
                                  >
                                    Save
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenges" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowChallengeDialog(true)} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-2" />
                New Challenge
              </Button>
            </div>

            {challenges?.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <Target className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No challenges assigned yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingChallenges.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">Active</h3>
                    {pendingChallenges.map((challenge) => (
                      <Card key={challenge.challenge_id} className="border-amber-200 bg-amber-50 mb-3">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-neutral-900">{challenge.title}</p>
                              <p className="text-sm text-neutral-600 mt-1">{challenge.description}</p>
                              <p className="text-xs text-amber-700 mt-2">Due: {challenge.due_date}</p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {completedChallenges.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">Completed</h3>
                    {completedChallenges.map((challenge) => (
                      <Card key={challenge.challenge_id} className="border-green-200 bg-green-50 mb-3">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-neutral-900">{challenge.title}</p>
                              <p className="text-xs text-green-700 mt-2">
                                Completed: {new Date(challenge.completed_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Done
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Contact Dialog */}
      <Dialog open={showEditContactDialog} onOpenChange={setShowEditContactDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Client Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editContactForm.name}
                onChange={(e) => setEditContactForm({ ...editContactForm, name: e.target.value })}
                placeholder="Client name"
                data-testid="edit-contact-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editContactForm.email}
                onChange={(e) => setEditContactForm({ ...editContactForm, email: e.target.value })}
                placeholder="client@example.com"
                data-testid="edit-contact-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={editContactForm.phone}
                onChange={(e) => setEditContactForm({ ...editContactForm, phone: e.target.value })}
                placeholder="(555) 123-4567"
                data-testid="edit-contact-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Business Name</Label>
              <Input
                value={editContactForm.business_name}
                onChange={(e) => setEditContactForm({ ...editContactForm, business_name: e.target.value })}
                placeholder="Business name"
                data-testid="edit-contact-business"
              />
            </div>
            <Button onClick={handleSaveContact} disabled={savingContact} className="w-full bg-[#39FF14] hover:bg-[#32E010]" data-testid="save-contact-btn">
              {savingContact ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Intake Dialog */}
      <Dialog open={showEditIntakeDialog} onOpenChange={setShowEditIntakeDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Intake Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Textarea
                value={editIntakeForm.purpose}
                onChange={(e) => setEditIntakeForm({ ...editIntakeForm, purpose: e.target.value })}
                placeholder="Why is the client seeking consulting?"
                rows={2}
                data-testid="edit-intake-purpose"
              />
            </div>
            <div className="space-y-2">
              <Label>Motivation</Label>
              <Textarea
                value={editIntakeForm.motivation}
                onChange={(e) => setEditIntakeForm({ ...editIntakeForm, motivation: e.target.value })}
                placeholder="What motivates them to make changes?"
                rows={2}
                data-testid="edit-intake-motivation"
              />
            </div>
            <div className="space-y-2">
              <Label>Primary Business Goal</Label>
              <Textarea
                value={editIntakeForm.primary_business_goal}
                onChange={(e) => setEditIntakeForm({ ...editIntakeForm, primary_business_goal: e.target.value })}
                placeholder="Main goal they want to achieve"
                rows={2}
                data-testid="edit-intake-goal"
              />
            </div>
            <div className="space-y-2">
              <Label>Consulting Expectations</Label>
              <Textarea
                value={editIntakeForm.consulting_expectations}
                onChange={(e) => setEditIntakeForm({ ...editIntakeForm, consulting_expectations: e.target.value })}
                placeholder="What do they expect from the consulting engagement?"
                rows={2}
                data-testid="edit-intake-expectations"
              />
            </div>
            <Button onClick={handleSaveIntake} disabled={savingIntake} className="w-full bg-[#39FF14] hover:bg-[#32E010]" data-testid="save-intake-btn">
              {savingIntake ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Reset Client Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
              <p>You are resetting the password for <strong>{data?.client?.name || data?.client?.email}</strong>.</p>
              <p className="mt-1">The client will need to use this new password to log in.</p>
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                data-testid="new-password-input"
              />
              <p className="text-xs text-neutral-500">Password must be at least 8 characters</p>
            </div>
            <Button 
              onClick={handleResetPassword} 
              disabled={resettingPassword || newPassword.length < 8} 
              className="w-full bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-reset-password-btn"
            >
              {resettingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting...</> : <><Key className="w-4 h-4 mr-2" />Reset Password</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetailPage;
